import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Building2, Loader2 } from "lucide-react";

import { formatDateTime, errorText } from "./format";
import OrgSelect from "./OrgSelect";
import { draftRuntime } from "./runtime";
import {
  reasonLabel,
  RUNTIME_META,
  type SignalAdminDraft,
  type SignalAdminOrganization,
  type SignalAdminRegistry,
  type SignalTrace,
  TRACE_RESULT_META,
  type TraceResult,
  TIER_META,
} from "./types";
import { useAppDispatch } from "../../Store/Hooks";
import { fetchSignalAdminTraces } from "../../Store/Reducers/SignalAdminSlice";

interface TracesPanelProps {
  orgId: string;
  organizations: SignalAdminOrganization[];
  orgsLoading: boolean;
  onOrgChange: (orgId: string) => void;
  registry: SignalAdminRegistry | null;
  draft: SignalAdminDraft;
}

const selectClass =
  "h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-[12.5px] text-gray-800 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20";

const describeReason = (
  trace: SignalTrace,
  layerLabel: (id: string) => string
): string => {
  if (!trace.reason) return "—";
  if (trace.result === "delivered") return trace.reason;
  if (trace.reason === "organization_inactive") return "Organization Inactive";
  if (trace.reason.startsWith("actions_surface_"))
    return `Open Actions ${trace.reason.replace("actions_surface_", "")}`;
  return reasonLabel(trace.reason, layerLabel);
};

const TracesPanel: React.FC<TracesPanelProps> = ({
  orgId,
  organizations,
  orgsLoading,
  onOrgChange,
  registry,
  draft,
}) => {
  const [traces, setTraces] = useState<SignalTrace[]>([]);
  const [accounts, setAccounts] = useState<
    { account_id: string; name: string }[]
  >([]);
  const [account, setAccount] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedRun, setSelectedRun] = useState<string | null>(null);
  const dispatch = useAppDispatch();

  const load = useCallback(async () => {
    if (!orgId) {
      setTraces([]);
      setAccounts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const params: Record<string, string> = {};
      if (account) params.account_id = account;
      if (result) params.result = result;
      const res = await dispatch(
        fetchSignalAdminTraces({ orgId, params })
      ).unwrap();
      setTraces(res?.traces ?? []);
      setAccounts(res?.accounts ?? []);
    } catch (e) {
      setError(errorText(e, "Couldn't load evaluation traces."));
    } finally {
      setLoading(false);
    }
  }, [dispatch, orgId, account, result]);

  useEffect(() => {
    setAccount("");
    setResult("");
    setSelectedRun(null);
  }, [orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  const layerLabel = (id: string) =>
    registry?.layers.find(l => l.id === id)?.label ?? id;
  const runRows = useMemo(
    () => traces.filter(t => t.run_id === selectedRun),
    [traces, selectedRun]
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-[15px] font-bold text-gray-900">
              Evaluation Traces
            </h3>
            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-gray-500">
              Per organization
            </span>
          </div>
          <p className="mt-0.5 max-w-3xl text-[12.5px] text-gray-500">
            Every rule that matched in each evaluation run, and what platform
            controls did with it — delivered, hidden, not delivered, skipped or
            blocked — with the config version in force at the time. Recorded
            outcomes never change; kept for 60 days.
          </p>
        </div>
        <OrgSelect
          organizations={organizations}
          value={orgId}
          onChange={onOrgChange}
          loading={orgsLoading}
          emptyLabel="Select organization…"
        />
      </div>

      {!orgId ? (
        <div className="flex flex-col items-center rounded-xl border border-dashed border-gray-300 bg-gray-50/60 px-6 py-14 text-center">
          <Building2 className="h-8 w-8 text-gray-300" aria-hidden />
          <p className="mt-3 text-[14px] font-semibold text-gray-800">
            Select an organization
          </p>
          <p className="mt-1 max-w-sm text-[13px] text-gray-500">
            Evaluation traces are stored per organization. Platform policy does
            not require a selection.
          </p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <select
              aria-label="Account"
              value={account}
              onChange={e => setAccount(e.target.value)}
              className={selectClass}
            >
              <option value="">All accounts</option>
              {accounts.map(a => (
                <option key={a.account_id} value={a.account_id}>
                  {a.name}
                </option>
              ))}
            </select>
            <select
              aria-label="Result"
              value={result}
              onChange={e => setResult(e.target.value)}
              className={selectClass}
            >
              <option value="">All results</option>
              {Object.entries(TRACE_RESULT_META).map(([id, meta]) => (
                <option key={id} value={id}>
                  {meta.label}
                </option>
              ))}
            </select>
            <span className="ml-auto text-xs text-gray-500">
              {loading ? "Loading…" : `${traces.length} trace records`}
            </span>
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
              {error}
            </div>
          )}

          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
            {loading && traces.length === 0 ? (
              <div className="flex items-center justify-center gap-2 px-5 py-10 text-[12.5px] text-gray-400">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Loading traces…
              </div>
            ) : traces.length === 0 ? (
              <p className="px-5 py-10 text-center text-[12.5px] text-gray-500">
                No traces yet. They&apos;re recorded from the next time each
                account is evaluated.
              </p>
            ) : (
              <table className="w-full min-w-[900px] border-collapse text-left">
                <thead>
                  <tr className="bg-gray-50 text-[10px] font-bold uppercase tracking-[0.07em] text-gray-400">
                    <th className="px-4 py-2">When</th>
                    <th className="px-3 py-2">Account</th>
                    <th className="px-3 py-2">Signal</th>
                    <th className="px-3 py-2">Result</th>
                    <th className="px-3 py-2">Reason / evidence</th>
                    <th className="px-4 py-2">Config</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-[12.5px]">
                  {traces.map((t, i) => (
                    <tr
                      key={`${t.run_id}-${t.definition_id}-${i}`}
                      onClick={() =>
                        setSelectedRun(prev =>
                          prev === t.run_id ? null : t.run_id
                        )
                      }
                      className={`cursor-pointer align-top ${
                        selectedRun === t.run_id
                          ? "bg-brand-faint shadow-[inset_3px_0_0_#9333EA]"
                          : "hover:bg-gray-50"
                      }`}
                    >
                      <td className="whitespace-nowrap px-4 py-2.5 text-gray-500">
                        {formatDateTime(t.evaluated_at)}
                      </td>
                      <td className="px-3 py-2.5 font-semibold text-gray-900">
                        {t.account_name}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="text-gray-900">{t.title}</div>
                        <div className="font-mono text-[10.5px] text-gray-400">
                          {t.definition_id}
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <span
                          className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[10.5px] font-bold ring-1 ring-inset ${TRACE_RESULT_META[t.result as TraceResult]?.className ?? ""}`}
                        >
                          {TRACE_RESULT_META[t.result as TraceResult]?.label ??
                            t.result}
                        </span>
                      </td>
                      <td className="max-w-[280px] px-3 py-2.5 font-mono text-[11px] text-gray-500">
                        {describeReason(t, layerLabel)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 font-mono text-[11px] text-gray-500">
                        {t.config_version > 0
                          ? `v${t.config_version}`
                          : "Default"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {selectedRun && runRows.length > 0 && (
            <section className="rounded-xl border border-gray-200 bg-white">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-5 py-3">
                <h4 className="text-[13px] font-bold text-gray-900">
                  Run on {runRows[0].account_name} ·{" "}
                  {formatDateTime(runRows[0].evaluated_at)}
                </h4>
                <span className="text-[11.5px] text-gray-400">
                  {runRows[0].triggered_by ?? "—"} · config{" "}
                  {runRows[0].config_version > 0
                    ? `v${runRows[0].config_version}`
                    : "default"}
                </span>
              </div>
              <div className="px-5 py-4">
                <h5 className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.08em] text-gray-400">
                  Recorded vs. current controls
                </h5>
                <p className="mb-3 text-[12px] text-gray-500">
                  Compares what was recorded with what today&apos;s controls
                  (including unapplied draft changes) would do. It doesn&apos;t
                  re-run the rules against current data.
                </p>
                <ul className="flex flex-col gap-2">
                  {runRows.map(t => {
                    const def = registry?.definitions.find(
                      d => d.id === t.definition_id
                    );
                    const now = def ? draftRuntime(def, draft) : null;
                    return (
                      <li
                        key={t.definition_id}
                        className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-gray-200 px-3 py-2 text-[12.5px]"
                      >
                        {t.tier && (
                          <span
                            className={`rounded px-1.5 py-0.5 text-[10.5px] font-bold ${TIER_META[t.tier].className}`}
                          >
                            {TIER_META[t.tier].label}
                          </span>
                        )}
                        <span className="font-semibold text-gray-900">
                          {t.title}
                        </span>
                        <span className="ml-auto text-gray-500">
                          {TRACE_RESULT_META[t.result as TraceResult]?.label ??
                            t.result}
                        </span>
                        <span className="text-gray-300">→</span>
                        <span className="font-semibold text-gray-800">
                          {now ? RUNTIME_META[now].label : "—"}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default TracesPanel;
